import { ValidationError } from 'common/errors';
import { propertyIsDefined } from 'common/type-guards';
import {
  getFullAccessToken,
  getIsFeatureToggleEnabled,
  getMerchantConfig
} from 'communicator/store/selectors';
import { markStart } from 'communicator/utils/instrumentation';
import { AuthenticationDecision } from 'types/data';
import { AuthenticationMethod } from 'types/emvco';
import { CheckoutCommunicatorPayload, CheckoutRequest } from 'types/visa-sdk/checkout';
import checkoutIntegratedEnrollCard from './enroll-card';
import checkoutIntegratedSelectCard from './select-card';

// Assume collectShipping false for integrated!!

/**
 * Performs all the functionality of the DCF for merchants configured for
 * integrated checkout. This includes:
 *
 * 1. check complianceSettings
 * 2. If missing data, throw error (exceptions exist for TnC/PP)
 *
 * Otherwise, if checkout data is correct
 * 1. POST /consumer-api/v2/account/ARMGUID - createAccount
 * 2. generate access_token
 * 3. PUT /intent
 *
 * If stepup, launch DCF on stepup screen.
 *
 * Otherwise,
 * 1. Spawn remember_me
 * 2. Spawn activity_ref
 * 3. /enrollStaySignedIn
 * 4. Spawn secured_id_token
 * 5. Complete checkout
 */
export default async function integratedCheckout(
  request: CheckoutRequest,
  launchSRCBankApp: (authenticationMethod: AuthenticationMethod) => void,
  launchSRCIssuerOtp: (authenticationMethod: AuthenticationMethod) => void,
  launchCvv: () => void,
  launchDcf: () => void,
  launchFIDO: (fidoAppScreen: 'auth' | 'register') => void,
  launchThreeDs: (idvUrl: string) => void,
  launchThreeDsWithOnLoad: (idUrl: string) => void,
  launchVPP: (
    requestPayload: string,
    checkoutUrl: string,
    authDecision: AuthenticationDecision
  ) => void
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const userType = getFullAccessToken() ? '_RETURNING' : '_NEW';
  markStart(`CHECKOUT_INTEGRATED`);
  markStart(`CHECKOUT_INTEGRATED_ENROLL_CARD${userType}`);
  markStart(`CHECKOUT_INTEGRATED_LEGACY_ENROLL_CARD${userType}`);
  const checkoutData = request.data;
  const merchantConfig = getMerchantConfig();

  if (checkoutData.srcDigitalCardId || !propertyIsDefined(checkoutData, 'encryptedCard')) {
    if (!getIsFeatureToggleEnabled('enableNonDcfCvv')) {
      request.launchHandlers.launchWindowRefLoader();
    }

    return checkoutIntegratedSelectCard(
      checkoutData,
      launchSRCBankApp,
      launchSRCIssuerOtp,
      launchCvv,
      launchDcf,
      launchFIDO,
      launchThreeDs,
      launchThreeDsWithOnLoad,
      launchVPP,
      request,
      request.meta
    );
  }

  request.launchHandlers.launchLoader(merchantConfig.displayName);
  return checkoutIntegratedEnrollCard(
    checkoutData,
    launchCvv,
    launchDcf,
    launchSRCBankApp,
    launchSRCIssuerOtp
  );
}
